import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ScanLine, AlertTriangle } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/usher"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      navigate(res.user.role === "admin" ? "/admin" : "/usher");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative bg-[#121212] text-white p-12 flex-col justify-between border-r-2 border-[#121212]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FFCC00] border-2 border-white flex items-center justify-center">
            <ScanLine className="w-6 h-6 text-[#121212]" strokeWidth={2.5} />
          </div>
          <div className="font-display text-2xl font-black tracking-tighter uppercase">
            Gatekeep
          </div>
        </div>

        <div>
          <div className="mono-label text-[#FFCC00] mb-4">// EVENT CONTROL</div>
          <h1 className="font-display font-black uppercase text-5xl xl:text-7xl tracking-tighter leading-[0.9]">
            Scan.
            <br />
            Verify.
            <br />
            <span className="text-[#FFCC00]">Admit.</span>
          </h1>
          <p className="mt-6 text-base text-[#A1A1AA] max-w-md leading-relaxed">
            High-throughput QR ticketing for live events. Generate tickets in
            bulk, distribute them off-platform, then verify entry at the door.
          </p>
        </div>

        <div className="mono-label text-[#A1A1AA]">
          v1.0 / Event Ticket Scanner
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F4F4F5]">
        <div className="w-full max-w-md">
          <div className="mono-label mb-2 text-[#666]">// AUTHENTICATION</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-8">
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="mono-label block mb-2" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="brut-input"
                placeholder="admin@event.com"
                required
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="mono-label block mb-2" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="brut-input"
                placeholder="••••••••"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="border-2 border-[#FF3B30] bg-white p-3 flex items-start gap-2"
                data-testid="login-error"
              >
                <AlertTriangle className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="brut-btn-accent w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? <div className="brut-spinner !w-5 !h-5" /> : "Enter"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
